import { Link } from "react-router-dom";
import { ArrowRight, Plus } from "lucide-react";
import { IMG, KWILA_FAQS } from "@/data/site";
import QuoteCTA from "@/components/QuoteCTA";
import FlyerCard from "@/components/FlyerCard";
import { Reveal, Kicker, MaskedLine } from "@/components/Reveal";

const SECTIONS = [
  {
    title: "Why Kwila for outdoor furniture?",
    body: [
      "Kwila is durable, termite resistant and naturally dense, with a golden to dark brown colour marked by yellow and black flecks. Its abundant natural oils, minimal shrinkage and natural resistance to splitting mean Kwila furniture can live permanently outdoors in harsh climates — it even withstands salt water.",
      "Kwila has long been used for boat building, marine jetties, flooring, decking and staircases. The Australian Department of Primary Industries has rated Kwila as Class/ Rating One — the highest suitability rating for timber outdoor furniture [historical rating — to be confirmed by client].",
    ],
  },
  {
    title: "Common uses",
    body: [
      "Outdoor furniture · Decking · Flooring · Stairs · Joinery · Solid timber doors · Structural and decorative timber.",
    ],
  },
  {
    title: "Natural colour & grain variation",
    body: [
      "Kwila is a natural timber product. Grain, tone, colour and natural imperfections vary from piece to piece — this variation is part of the character of the timber, not a defect.",
    ],
  },
  {
    title: "Tannin bleed — read before you position",
    body: [
      "Fresh Kwila can release natural tannins when wet. This may temporarily stain nearby paving, tiles or concrete. Follow the care guide before placing new Kwila furniture or timber in its final position.",
    ],
  },
  {
    title: "Care & oiling",
    body: [
      "Clean before final positioning. Hose down and allow tannins to release in a suitable area. Oil regularly — for outdoor furniture, oiling every 3 months may be recommended depending on exposure and conditions. [Care product recommendations to be confirmed by client.]",
    ],
  },
];

export default function KwilaMerbau() {
  return (
    <div data-testid="kwila-merbau-page">
      <section className="relative overflow-hidden px-5 pb-20 pt-32 md:px-10 md:pt-44">
        <div className="mx-auto grid max-w-[1500px] items-end gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Kicker>Know Your Timber</Kicker>
            <h1 className="mt-4 font-display text-6xl font-light leading-[0.92] tracking-tight md:text-8xl">
              <MaskedLine delay={0.1}>What is</MaskedLine>
              <MaskedLine delay={0.22} className="italic text-kwila">Kwila?</MaskedLine>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/70 md:text-lg">
              Kwila, also widely known as Merbau, is the common name used for <em>Intsia bijuga</em> — a tropical
              hardwood found across Indonesia, New Guinea and the South Pacific. It answers to many names around
              the world: Afzelia, Borneo Teak, Hintzy (Madagascar), Ipil (Philippines) and Ironwood (UK). Its
              recorded hardness of 8.6 sits above Jarrah (8.5) and far above Teak (4.5) [historical figures — to
              be confirmed by client], and it is used in outdoor furniture, decking, flooring, joinery, solid
              timber doors and structural timber.
            </p>
            <Reveal delay={0.35} className="mt-8 max-w-md">
              <FlyerCard />
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <div className="overflow-hidden rounded-t-[160px]">
                <img src={IMG.rawWood} alt="Kwila / Merbau timber grain close-up" className="aspect-[4/5] w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>


      <section className="px-5 py-24 md:px-10" data-testid="kwila-sections">
        <div className="mx-auto max-w-[1100px]">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.title} delay={0.05} className="grid gap-4 border-t border-ink/10 py-10 md:grid-cols-12 md:gap-8">
              <div className="md:col-span-4">
                <span className="text-xs font-bold text-kwila">0{i + 1}</span>
                <h2 className="mt-1 font-display text-3xl tracking-tight">{s.title}</h2>
              </div>
              <div className="space-y-4 text-base leading-relaxed text-ink/70 md:col-span-8">
                {s.body.map((p) => (
                  <p key={p.slice(0, 24)}>{p}</p>
                ))}
              </div>
            </Reveal>
          ))}
          <Reveal className="mt-6 rounded-sm border border-forest/30 bg-forest/5 p-6 md:p-8" data-testid="sourcing-note">
            <p className="text-sm leading-relaxed text-ink/75 md:text-base">
              <span className="font-bold text-forest">Responsible sourcing note.</span> Mr Kwila is committed to
              responsible timber sourcing. Final certification, legality, chain-of-custody or sustainability
              documentation is to be supplied by the client and will be added before launch.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-stone/60 px-5 py-24 md:px-10" data-testid="kwila-faq">
        <div className="mx-auto max-w-[1100px]">
          <Reveal className="mb-10">
            <Kicker>Quick Answers</Kicker>
            <h2 className="mt-3 font-display text-3xl font-light tracking-tight md:text-5xl">
              Kwila questions, answered
            </h2>
          </Reveal>
          <div className="divide-y divide-ink/10 border-y border-ink/10">
            {KWILA_FAQS.map((f, i) => (
              <details key={f.q} className="group py-5" data-testid={`faq-item-${i}`}>
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
                  <span className="font-display text-xl tracking-tight md:text-2xl">{f.q}</span>
                  <Plus size={18} className="shrink-0 text-kwila transition-transform duration-300 group-open:rotate-45" />
                </summary>
                <p className="mt-3 max-w-3xl text-sm leading-relaxed text-ink/70 md:text-base">{f.a}</p>
              </details>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Link
              to="/shop?division=timber"
              data-testid="kwila-shop-timber-link"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-ink px-8 py-3.5 text-xs font-bold uppercase tracking-[0.14em] text-cream transition-colors hover:bg-kwila"
            >
              Shop Kwila Timber <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      <QuoteCTA title="Ready to work with Kwila?" copy="Furniture, decking, doors or raw supply — one quote request covers it all." />
    </div>
  );
}
